'use client';

import { ErrorKeys } from '@/@shared/types/errorKeys';
import {
	useCreateAuthEmailSendMutation,
	useCreateAuthEmailVerifyMutation,
	useCreateAuthSignupEmailMutation,
} from '@/domains/auth/api/auth/useAuthMutations';
import type {
	AuthController_sendEmail_Body,
	AuthController_signupEmail_Body,
	AuthController_verifyEmail_Body,
} from '@/domains/auth/types/validated';
import { LOGIN_PATHNAME } from '@/legacy/constants/pathname';
import { signUpSchema, type SignUpType } from '@/model/zod/authZod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

export const useSignupForm = (locale: string) => {
	const router = useRouter();

	const steps = ['RegisterAgreements', 'GetEmail', 'VerifyEmail', 'GetPasswords', 'RegisterDone'] as const;

	const [currentStep, setCurrentStep] = useState<number>(0);
	const [direction, setDirection] = useState<'left' | 'right'>('left');
	const [verificationToken, setVerificationToken] = useState('');

	// React Hook Form
	const methods = useForm<SignUpType>({
		criteriaMode: 'all',
		mode: 'onChange',
		resolver: zodResolver(signUpSchema),
	});

	// Mutations - domains에서 가져온 것들 (toast 비활성화)
	const sendEmailMutation = useCreateAuthEmailSendMutation();
	const verifyEmailMutation = useCreateAuthEmailVerifyMutation();
	const signupMutation = useCreateAuthSignupEmailMutation();

	// 이메일 전송
	const sendVerificationEmail = async (email: string) => {
		// 이전 에러 클리어 및 인증 상태 리셋
		methods.clearErrors('getEmail');
		methods.clearErrors('verifyEmail');
		methods.setValue('verifyStatus', false);

		const body: AuthController_sendEmail_Body = {
			email,
			hl: locale,
			type: 'EMAIL_VERIFY' as any,
		};

		const result = await sendEmailMutation.mutateAsync(body);

		// ⭐ 에러 처리 (throwOnError: false이므로 result.error에 담김)
		if ('error' in result && result.error) {
			const errorData = result.error as { error?: string; message?: string };

			// DUPLICATED_EMAIL 에러인 경우
			if (errorData.error === 'DUPLICATED_EMAIL') {
				methods.setError('getEmail', {
					type: 'manual',
					message: ErrorKeys.server.duplicatedEmail,
				});
				return false;
			}

			// 기타 에러
			methods.setError('getEmail', {
				type: 'manual',
				message: ErrorKeys.server.emailDeliveryFailed,
			});
			return false;
		}

		// ⭐ 성공
		if (result?.data?.token) {
			setVerificationToken(result.data.token);
			return true;
		}

		return false;
	};

	// 이메일 인증
	const verifyEmailCode = async (code: string) => {
		// 이전 에러 클리어
		methods.clearErrors('verifyEmail');

		const body: AuthController_verifyEmail_Body = {
			code,
			token: verificationToken,
		};

		const result = await verifyEmailMutation.mutateAsync(body);

		// ⭐ 에러 처리
		if ('error' in result && result.error) {
			const errorData = result.error as { error?: string; message?: string };

			// VERIFICATION_CODE_MISMATCH 에러인 경우
			if (errorData.error === 'VERIFICATION_CODE_MISMATCH') {
				methods.setError('verifyEmail', {
					type: 'manual',
					message: ErrorKeys.authCode.verifyError,
				});
				return false;
			}

			// 기타 에러
			methods.setError('verifyEmail', {
				type: 'manual',
				message: ErrorKeys.authCode.verifyError,
			});
			return false;
		}

		// ⭐ 성공
		if (result?.data?.status === true) {
			methods.setValue('verifyStatus', true);
			return true;
		}

		return false;
	};

	// 최종 회원가입
	const submitSignup = async (formData: SignUpType) => {
		// 이전 에러 클리어
		methods.clearErrors();

		const body = {
			email: formData.getEmail,
			password: formData.getPasswords.getPassword,
			marketingTerms: formData.registerAgreements.marketing,
		} as AuthController_signupEmail_Body;

		const result = await signupMutation.mutateAsync(body);

		// ⭐ 에러 처리
		if ('error' in result && result.error) {
			const errorData = result.error as { error?: string; message?: string };

			// DUPLICATED_EMAIL 에러인 경우
			if (errorData.error === 'DUPLICATED_EMAIL') {
				methods.setError('getEmail', {
					type: 'manual',
					message: ErrorKeys.server.duplicatedEmail,
				});
				return false;
			}

			// 기타 에러는 비밀번호 필드에 표시
			methods.setError('getPasswords.getPassword', {
				type: 'manual',
				message: ErrorKeys.server.unknownError,
			});
			return false;
		}

		// ⭐ 성공
		if (result?.data) {
			// 성공 시 완료 단계로
			nextStep();
			return true;
		}

		return false;
	};

	// 다음 단계로 이동
	const nextStep = async () => {
		const stepName = steps[currentStep];
		const isValid = await methods.trigger(stepName as keyof SignUpType);

		if (isValid && currentStep < steps.length - 1) {
			setDirection('right');
			setTimeout(() => {
				setCurrentStep((prev) => prev + 1);
			}, 100);
		} else {
			console.log('Validation errors:', methods.formState.errors);
		}
	};

	// 완료 후 로그인으로 이동
	const goToLogin = () => {
		router.push(LOGIN_PATHNAME(locale));
	};

	return {
		// State
		currentStep: steps[currentStep],
		currentStepIndex: currentStep,
		direction,
		verificationToken,

		// Form methods
		methods,

		// Actions
		nextStep,
		sendVerificationEmail,
		verifyEmailCode,
		submitSignup,
		goToLogin,

		// Loading states
		isSendingEmail: sendEmailMutation.isPending,
		isVerifying: verifyEmailMutation.isPending,
		isSubmitting: signupMutation.isPending,
	};
};
